/**
 * Presentation defaults, in one place.
 *
 * These were previously inline `?? 'medium'` / `!== false` / `?? 0` expressions
 * scattered through the presenter, which meant the defaults existed twice — here
 * and in `panelPresentationSchema`'s `.default(...)` calls in
 * `packages/db-types/src/panel-definition.ts` — with nothing keeping them equal.
 * `tests/architecture/panel-schema-mirror.test.ts` now pins this object against
 * `panelPresentationSchema.parse({})`, so a default changed on one side fails CI.
 */
import type { PanelAnimation, PanelPresentation } from './types';

/**
 * Today's hard-coded modal entrance, now expressed as data. Every value here
 * must keep reproducing what `@keyframes fias-panel-in` drew before the field
 * existed, or a catalog authored with the defaults would look different on a
 * renderer that predates it.
 */
export const DEFAULT_PANEL_ANIMATION: Required<PanelAnimation> = {
  type: 'fade-rise',
  durationMs: 140,
  easing: 'ease-out',
};

export const DEFAULT_PANEL_PRESENTATION: Required<PanelPresentation> = {
  size: 'medium',
  dismissible: true,
  priority: 0,
  animation: DEFAULT_PANEL_ANIMATION,
};

/** Fill a partial presentation hint with the defaults above. */
export function resolvePanelPresentation(
  ...overrides: ReadonlyArray<PanelPresentation | undefined>
): Required<PanelPresentation> {
  const resolved = { ...DEFAULT_PANEL_PRESENTATION };
  for (const override of overrides) {
    if (!override) continue;
    if (override.size !== undefined) resolved.size = override.size;
    if (override.dismissible !== undefined) resolved.dismissible = override.dismissible;
    if (override.priority !== undefined) resolved.priority = override.priority;
    // Merged field-by-field, not replaced: a caller overriding only `durationMs`
    // must not silently reset `type` and `easing` to the defaults.
    if (override.animation !== undefined) {
      resolved.animation = { ...resolved.animation, ...override.animation };
    }
  }
  return resolved;
}
